import React from 'react';
import { Result, Skeleton } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { DefaultPageLayout } from 'layouts/default-page-layout';
import { useGetTripsLoadAggregatedStatistics } from 'api/statistic/use-get-trips-load-aggregated-statistics';
import { useGetTripsLoadStatistics } from 'api/statistic/use-get-trips-load-statistics';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard';
import { useTransportLoadFiltersStore } from 'shared/stores/use-transport-load-filters';
import { LinearChart } from 'components/insights/linear-chart';
import { TransportAggregatedLoadStatistic } from 'components/transport-load/transport-aggregated-load-statistic';
import { TransportLoadFilters } from 'components/transport-load/transport-load-filters';

export const TransportLoadPageComponent: React.FC = () => {
  const { filters } = useTransportLoadFiltersStore();
  const { data: aggregatedStatisticsData, isFetching: isAggregatedStatisticsFetching } =
    useGetTripsLoadAggregatedStatistics({ filters });
  const { data: statisticsData, isFetching: isStatisticsFetching } = useGetTripsLoadStatistics({ filters });

  const isFetching = isAggregatedStatisticsFetching || isStatisticsFetching;

  const mainSeries = [
    {
      name: 'Average load',
      data: statisticsData?.series?.averageLoad ?? [],
      unit: '%',
    },
    {
      name: 'Number of trips',
      data: statisticsData?.series?.numberOfTrips ?? [],
      relatedToOppositeYAxis: true,
    },
  ];

  const comparisonSeries = statisticsData?.comparisonSeries
    ? [
        {
          name: 'Average load',
          data: statisticsData.comparisonSeries.averageLoad ?? [],
        },
        {
          name: 'Number of trips',
          data: statisticsData.comparisonSeries.numberOfTrips ?? [],
          relatedToOppositeYAxis: true,
        },
      ]
    : undefined;

  const header = <PageHeader title={<TransportLoadFilters />} />;

  return (
    <DefaultPageLayout header={header}>
      {!filters.userId ? (
        <Result
          icon={<UserOutlined />}
          title='Select a manager'
          subTitle='Please select a manager to view their transport load statistics'
          status='info'
        />
      ) : (
        <Skeleton loading={isFetching}>
          {aggregatedStatisticsData && (
            <TransportAggregatedLoadStatistic
              value={aggregatedStatisticsData.value}
              comparisonValue={aggregatedStatisticsData?.comparisonValue}
            />
          )}
          <LinearChart
            title='Linear chart'
            mainSeries={mainSeries}
            comparisonSeries={comparisonSeries}
            withOppositeYAxis
          />
        </Skeleton>
      )}
    </DefaultPageLayout>
  );
};

export const TransportLoadPage = withRoleGuard(TransportLoadPageComponent, [
  UserRole.admin,
  UserRole.transport_manager,
]);
